import { useState, useEffect } from 'react'
import { Trophy, Calendar, MapPin, Users, Award, List, TrendingUp, FileText, Scale, Info, Heart, Clock, Mail, Target, ChevronDown, ChevronUp, LayoutGrid, LogIn, UserPlus, Lock } from 'lucide-react'
import CategoryView from './CategoryView'
import BracketView from './BracketView'
import ScheduleView from './ScheduleView'
import { trackTabSwitch, trackLiveScoresActivity, trackEmailVerification } from '../config/analytics'

const STORAGE_KEY = 'tournament-viewer-selected-category'
const TAB_STORAGE_KEY = 'tournament-viewer-selected-tab'

function TournamentView({ data, colorTheme, themeClasses, isDarkMode = true, currentUser, registrationCounts = {}, onRefresh, onAuthRequired, onRegister, onResendVerification, resendLoading, resendSuccess, resendError, categoryCache, fetchCategoryData, onCategoryChange }) {
  // Initialize from localStorage if available
  const [selectedCategoryId, setSelectedCategoryId] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY)
    } catch {
      return null
    }
  })
  
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const savedTab = localStorage.getItem(TAB_STORAGE_KEY)
      const isActive = data.tournament.status === 'in-progress' || 
                       data.tournament.status === 'in_progress' || 
                       data.tournament.status === 'live' || 
                       data.tournament.status === 'completed' || 
                       data.tournament.status === 'finished'
      const isRegClosed = data.tournament.status === 'registration_closed'
      const hasBrackets = isRegClosed && data.categories?.some(c => c.brackets && c.brackets.length > 0)
      // Default to 'brackets' only if registration_closed with bracket data, 'results' if active, otherwise 'info'
      return savedTab || (hasBrackets ? 'brackets' : isActive ? 'results' : 'info')
    } catch {
      return 'info'
    }
  })
  
  // Track which date groups are expanded (all expanded by default)
  const [expandedDateGroups, setExpandedDateGroups] = useState({})
  
  const { tournament, categories, exportedAt } = data
  
  // Group categories by start date
  const categoriesByDate = categories.reduce((groups, category) => {
    // Use startDate if available, otherwise fall back to tournament start date
    const startDate = category.startDate || tournament.startDate
    const dateKey = startDate ? new Date(startDate).toDateString() : 'No Date'
    
    if (!groups[dateKey]) {
      groups[dateKey] = []
    }
    groups[dateKey].push(category)
    return groups
  }, {})
  
  // Sort date groups chronologically
  const sortedDateGroups = Object.entries(categoriesByDate).sort((a, b) => {
    if (a[0] === 'No Date') return 1
    if (b[0] === 'No Date') return -1
    return new Date(a[0]) - new Date(b[0])
  })

  // Date keys for day filter
  const resultsDateKeys = sortedDateGroups.map(([k]) => k)
  const [resultsDay, setResultsDay] = useState(() => {
    const today = new Date().toDateString()
    return resultsDateKeys.includes(today) ? today : resultsDateKeys[0] || null
  })
  
  // Initialize all date groups as expanded on first render
  useEffect(() => {
    const initialExpanded = {}
    sortedDateGroups.forEach(([dateKey]) => {
      initialExpanded[dateKey] = true
    })
    setExpandedDateGroups(initialExpanded)
  }, []) // Only run once on mount
  
  // Toggle date group expansion
  const toggleDateGroup = (dateKey) => {
    setExpandedDateGroups(prev => ({
      ...prev,
      [dateKey]: !prev[dateKey]
    }))
  }
  
  // Sort categories within each date group by title, skill level, and age group
  const sortedCategories = [...categories].sort((a, b) => {
    // First, sort by title
    const titleCompare = (a.title || '').localeCompare(b.title || '')
    if (titleCompare !== 0) return titleCompare
    
    // Then by skill level order (if tournament has skill levels defined)
    if (tournament.skillLevels && tournament.skillLevels.length > 0) {
      const aSkillIndex = tournament.skillLevels.findIndex(sl => sl.name === a.skillLevel)
      const bSkillIndex = tournament.skillLevels.findIndex(sl => sl.name === b.skillLevel)
      
      // If both have valid skill levels, compare by order
      if (aSkillIndex !== -1 && bSkillIndex !== -1) {
        const skillCompare = aSkillIndex - bSkillIndex
        if (skillCompare !== 0) return skillCompare
      }
      // If only one has a valid skill level, prioritize it
      if (aSkillIndex !== -1 && bSkillIndex === -1) return -1
      if (aSkillIndex === -1 && bSkillIndex !== -1) return 1
    }
    
    // Finally, sort by age group
    return (a.ageGroup || '').localeCompare(b.ageGroup || '')
  })
  
  // Save to localStorage whenever selection changes
  useEffect(() => {
    try {
      if (selectedCategoryId) {
        localStorage.setItem(STORAGE_KEY, selectedCategoryId)
      } else {
        localStorage.removeItem(STORAGE_KEY)
      }
    } catch (err) {
      console.error('Failed to save category selection:', err)
    }
  }, [selectedCategoryId])

  // Sync category navigation with browser history so the back button works
  useEffect(() => {
    // On mount: if a category was restored from localStorage, ensure there is a
    // "list view" history entry BEFORE the category entry so the native back
    // button (including mobile swipe/hardware back) returns to the list rather
    // than exiting the app.
    if (selectedCategoryId) {
      window.history.replaceState({ categoryId: null }, '')        // list entry
      window.history.pushState({ categoryId: selectedCategoryId }, '') // category entry
    }

    const handlePopstate = (e) => {
      const id = e.state?.categoryId || null
      setSelectedCategoryId(id)
      onCategoryChange?.(id)
    }
    window.addEventListener('popstate', handlePopstate)
    return () => window.removeEventListener('popstate', handlePopstate)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const navigateBackToList = () => {
    setSelectedCategoryId(null)
    onCategoryChange?.(null)
    // Replace the current history entry so stale category state doesn't
    // accumulate — native back button still works via popstate
    window.history.replaceState({ categoryId: null }, '')
  }

  const navigateToCategory = (id) => {
    window.history.pushState({ categoryId: id }, '')
    setSelectedCategoryId(id)
    onCategoryChange?.(id)
    if (!categoryCache?.[id]) fetchCategoryData?.(id)
  }
  
  // Save active tab to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(TAB_STORAGE_KEY, activeTab)
    } catch (err) {
      console.error('Failed to save tab selection:', err)
    }
  }, [activeTab])

  // Track restricted access attempts
  useEffect(() => {
    const restrictedTabs = ['brackets', 'schedule', 'results']
    if (restrictedTabs.includes(activeTab)) {
      if (!currentUser) {
        trackEmailVerification('Auth Required', { label: activeTab })
      } else if (!currentUser.emailVerified) {
        trackEmailVerification('Restricted Access', { label: activeTab, user_email: currentUser.email })
      }
    }
  }, [activeTab, currentUser])
  
  // Get the current category from the latest data
  // Simply find it from the categories array - no deep clone needed
  const categoryToShow = selectedCategoryId 
    ? categories.find(c => c.id === selectedCategoryId)
    : null

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set'
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    } catch {
      return 'Invalid date'
    }
  }
  
  const formatDateTime = (dateString) => {
    if (!dateString) return 'Not set'
    try {
      return new Date(dateString).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
    } catch {
      return 'Invalid date'
    }
  }
  
  // Check if tournament is in progress
  const isInProgress = tournament.status === 'in-progress' || tournament.status === 'in_progress' || tournament.status === 'live'
  const isCompleted = tournament.status === 'completed' || tournament.status === 'finished'
  const isRegistrationClosed = tournament.status === 'registration_closed'
  const isActive = isInProgress || isCompleted
  
  // Determine the label for the results tab
  const resultsLabel = isCompleted ? 'Results' : 'Live Scores'
  
  // Build tabs array conditionally
  const baseTabs = isActive ? [
    { id: 'results', label: resultsLabel, icon: Trophy },
    { id: 'info', label: 'Tournament Info', icon: Info },
    { id: 'description', label: 'Description', icon: FileText }
  ] : [
    { id: 'info', label: 'Tournament Info', icon: Info },
    { id: 'description', label: 'Description', icon: FileText }
  ]
  
  // Add rules tab only if rules are defined
  if (tournament.rules && tournament.rules.trim() !== '') {
    baseTabs.push({ id: 'rules', label: 'Official Rules', icon: Scale })
  }
  
  // Add skill levels tab
  baseTabs.push({ id: 'skillLevels', label: 'Skill Levels', icon: Target })
  
  // Add brackets tab whenever bracket data exists (after Tournament Info)
  const hasBracketData = categories.some(c => c.brackets && c.brackets.length > 0)
  if (hasBracketData) {
    const infoIndex = baseTabs.findIndex(t => t.id === 'info')
    baseTabs.splice(infoIndex + 1, 0, { id: 'brackets', label: 'Brackets', icon: LayoutGrid })
  }
  
  // Add schedule tab if tournament is active or registration closed, and matches have scheduled times
  const hasScheduleData = (isActive || isRegistrationClosed) && categories.some(c => c.matches?.some(m => m.scheduledTime))
  if (hasScheduleData) {
    // Insert after brackets if brackets exist, otherwise after info
    const bracketsIndex = baseTabs.findIndex(t => t.id === 'brackets')
    const insertAfter = bracketsIndex !== -1 ? bracketsIndex + 1 : baseTabs.findIndex(t => t.id === 'info') + 1
    baseTabs.splice(insertAfter, 0, { id: 'schedule', label: 'Schedule', icon: Calendar })
  }
  
  // Add results tab at the end for inactive tournaments
  if (!isActive) {
    baseTabs.push({ id: 'results', label: resultsLabel, icon: Trophy })
  }
  
  const tabs = baseTabs

  // Show CategoryView if we have a selected ID and a category to show
  if (selectedCategoryId && categoryToShow) {
    const fullData = categoryCache?.[selectedCategoryId]
    if (!fullData) {
      // Brief loading state while per-category file fetches
      return (
        <div className={`min-h-screen ${themeClasses.bg} flex flex-col`}>
          <div className="p-4">
            <button
              onClick={navigateBackToList}
              className={`flex items-center gap-2 ${themeClasses.textMuted} hover:${themeClasses.text}`}
            >
              ← Back
            </button>
          </div>
          <div className="flex-1 flex items-center justify-center">
            <div className={`text-center ${themeClasses.textMuted}`}>
              <div className="w-8 h-8 border-2 border-current border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p>Loading matches...</p>
            </div>
          </div>
        </div>
      )
    }
    return (
      <CategoryView
        key={`category-${selectedCategoryId}-${exportedAt}`}
        category={{ ...categoryToShow, ...fullData }}
        tournament={tournament}
        colorTheme={colorTheme}
        themeClasses={themeClasses}
        isDarkMode={isDarkMode}
        exportedAt={exportedAt}
        onRefresh={onRefresh}
        onBack={navigateBackToList}
      />
    )
  }

  return (
    <div className={`min-h-screen ${themeClasses.bg} ${themeClasses.text} pb-20 relative transition-colors duration-500`}>
      {/* Premium Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute top-1/4 left-1/4 w-96 h-96 ${isInProgress ? 'bg-emerald-500/30' : 'bg-emerald-500/15'} rounded-full blur-3xl animate-pulse`}></div>
        <div className={`absolute bottom-1/4 right-1/4 w-96 h-96 ${isInProgress ? 'bg-blue-500/30' : 'bg-blue-500/15'} rounded-full blur-3xl animate-pulse`} style={{ animationDelay: '1s' }}></div>
        {isInProgress && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-emerald-400/25 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '0.5s' }}></div>
        )}
      </div>
      
      {/* Premium Header */}
      <div className={`${themeClasses.headerBg} sticky top-0 z-10 relative transition-all duration-500`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-5">
          <div className="flex items-center gap-3 sm:gap-5">
            {/* Logo with Premium Styling */}
            {tournament.logoUrl ? (
              <div className="relative group">
                <div className={`absolute -inset-1 bg-gradient-to-r ${themeClasses.accent} rounded-xl blur opacity-25 group-hover:opacity-40 transition-opacity duration-300`}></div>
                <img
                  src={tournament.logoUrl}
                  alt="Tournament Logo"
                  className={`relative w-14 h-14 sm:w-24 sm:h-24 md:w-28 md:h-28 lg:w-32 lg:h-32 object-contain rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-white'} p-1.5 sm:p-2 border ${themeClasses.border} flex-shrink-0`}
                  onError={(e) => e.target.style.display = 'none'}
                />
              </div>
            ) : (
              <div className="relative group">
                <div className={`absolute -inset-1 bg-gradient-to-r ${themeClasses.accent} rounded-xl blur opacity-40 group-hover:opacity-60 transition-opacity duration-300`}></div>
                <div className={`relative w-14 h-14 sm:w-24 sm:h-24 md:w-28 md:h-28 lg:w-32 lg:h-32 bg-gradient-to-br ${themeClasses.accent} rounded-xl flex items-center justify-center shadow-lg flex-shrink-0`}>
                  <Trophy className="w-8 h-8 sm:w-12 sm:h-12 md:w-14 md:h-14 text-white" />
                </div>
              </div>
            )}

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <h1 className={`text-base sm:text-2xl md:text-3xl font-black leading-tight tracking-tight ${themeClasses.text}`}>
                  {tournament.name}
                </h1>
                {colorTheme.name === 'Breast Cancer Awareness' && (
                  <svg className={`w-6 h-6 ${colorTheme.text.primary} animate-pulse`} viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C10.5 2 9.19 2.95 8.5 4.26C7.81 2.95 6.5 2 5 2C2.79 2 1 3.79 1 6C1 8.5 2.5 10.5 4.5 12.5L8.5 16.5C9.88 17.88 11.88 19 12 19C12.12 19 14.12 17.88 15.5 16.5L19.5 12.5C21.5 10.5 23 8.5 23 6C23 3.79 21.21 2 19 2C17.5 2 16.19 2.95 15.5 4.26C14.81 2.95 13.5 2 12 2M12 5.5C12.83 5.5 13.5 6.17 13.5 7C13.5 7.83 12.83 8.5 12 8.5C11.17 8.5 10.5 7.83 10.5 7C10.5 6.17 11.17 5.5 12 5.5Z"/>
                  </svg>
                )}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-3">
                <div className="hidden sm:flex items-center gap-2">
                  <svg className={`w-4 h-4 text-emerald-500`} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
                  </svg>
                  <span className={`text-sm font-semibold ${themeClasses.textMuted}`}>
                    Powered by Sports Control Hub
                  </span>
                </div>
                {tournament.status && (
                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold ${
                    tournament.status === 'live' || tournament.status === 'in-progress' || tournament.status === 'in_progress' 
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                      : tournament.status === 'registration_closed'
                      ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                      : tournament.status === 'upcoming' || tournament.status === 'scheduled'
                      ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                      : tournament.status === 'completed' || tournament.status === 'finished'
                      ? `${isDarkMode ? 'bg-white/10 text-gray-400 border border-white/10' : 'bg-gray-200 text-gray-700 border border-gray-300'}`
                      : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                  }`}>
                    <span className={`w-2 h-2 rounded-full ${
                      tournament.status === 'live' || tournament.status === 'in-progress' || tournament.status === 'in_progress' 
                        ? 'bg-emerald-400 animate-pulse' 
                        : tournament.status === 'registration_closed'
                        ? 'bg-orange-400'
                        : tournament.status === 'upcoming' || tournament.status === 'scheduled'
                        ? 'bg-blue-400'
                        : tournament.status === 'completed' || tournament.status === 'finished'
                        ? `${isDarkMode ? 'bg-gray-400' : 'bg-gray-600'}`
                        : 'bg-yellow-400'
                    }`}></span>
                    {tournament.status.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Main Content Area */}
          <div className="flex-1 min-w-0 order-1">
            <div className={`${themeClasses.glass} rounded-2xl sm:rounded-3xl overflow-hidden transition-all duration-500`}>
              {/* Premium Tab Navigation */}
              <div className={`relative border-b ${themeClasses.border}`}>
                <div className={`flex overflow-x-auto scrollbar-hide ${isDarkMode ? 'bg-black/20' : 'bg-gray-50/50'}`} style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                  {tabs.map((tab) => {
                    const Icon = tab.icon
                    const isActiveTab = activeTab === tab.id
                    return (
                      <button
                        key={tab.id}
                        onClick={() => {
                          setActiveTab(tab.id)
                          trackTabSwitch(tab.label, tournament?.id)
                        }}
                        title={tab.label}
                        className={`relative flex items-center justify-center gap-1.5 sm:gap-2.5 px-3 sm:px-6 py-3 sm:py-4 font-semibold text-xs sm:text-sm transition-all duration-300 whitespace-nowrap group ${
                          isActiveTab
                            ? `${themeClasses.text}`
                            : `${themeClasses.textMuted} ${themeClasses.hoverBg}`
                        }`}
                      >
                        <Icon className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 transition-transform duration-300 ${isActiveTab ? 'scale-110' : 'group-hover:scale-105'}`} />
                        <span className={`${isActiveTab ? 'inline' : 'hidden'} sm:inline`}>{tab.label}</span>
                        {/* Active indicator */}
                        {isActiveTab && (
                          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-emerald-500 to-teal-400"></div>
                        )}
                      </button>
                    )
                  })}
                </div>
                {/* Scroll hint fade on right edge - mobile only */}
                <div className={`sm:hidden absolute right-0 top-0 bottom-0 w-8 pointer-events-none bg-gradient-to-l ${isDarkMode ? 'from-black/40 to-transparent' : 'from-gray-100/80 to-transparent'}`}></div>
                {/* Tab position dots - mobile only */}
                <div className="flex items-center justify-center gap-1.5 py-1.5">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setActiveTab(tab.id)
                        trackTabSwitch(tab.label, tournament?.id)
                      }}
                      className={`rounded-full transition-all duration-300 ${
                        activeTab === tab.id
                          ? 'w-4 h-1.5 bg-emerald-400'
                          : `w-1.5 h-1.5 ${isDarkMode ? 'bg-white/20' : 'bg-gray-300'}`
                      }`}
                      aria-label={tab.label}
                    />
                  ))}
                </div>
              </div>
          
          {/* Tab Content */}
          <div className="p-5 sm:p-6 lg:p-8">
            {/* Brackets Tab */}
            {activeTab === 'brackets' && hasBracketData && (
              currentUser?.emailVerified ? (
                <div className="animate-fade-in">
                <BracketView
                  categories={categories}
                  tournament={tournament}
                  themeClasses={themeClasses}
                  isDarkMode={isDarkMode}
                  colorTheme={colorTheme}
                />
                </div>
              ) : currentUser && !currentUser.emailVerified ? (
                <div className={`animate-fade-in ${themeClasses.glassDark} rounded-2xl p-10 text-center space-y-4 transition-all duration-500`}>
                  <div className={`inline-flex items-center justify-center w-24 h-24 ${isDarkMode ? 'bg-gradient-to-br from-amber-500/20 to-amber-500/10' : 'bg-gradient-to-br from-amber-100 to-amber-50'} rounded-2xl mb-4 border-2 ${isDarkMode ? 'border-amber-500/30' : 'border-amber-300'}`}>
                    <Mail className={`w-12 h-12 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                  </div>
                  <h3 className={`text-2xl font-bold ${themeClasses.text}`}>Email Verification Required</h3>
                  <p className={`max-w-md mx-auto text-lg ${themeClasses.textMuted}`}>
                    Please verify your email address to view brackets.
                  </p>
                  <div className={`${isDarkMode ? 'bg-amber-500/10 border-amber-500/30' : 'bg-amber-50 border-amber-200'} border rounded-lg p-4 max-w-md mx-auto`}>
                    <p className={`text-xs ${isDarkMode ? 'text-amber-300' : 'text-amber-800'} mb-2 text-center`}>
                      Sent to: <span className="font-semibold">{currentUser.email}</span>
                    </p>
                    <p className={`text-xs ${themeClasses.textMuted} mb-3 text-center`}>
                      Check your inbox and spam folder
                    </p>
                    <button
                      onClick={onResendVerification}
                      disabled={resendLoading || resendSuccess}
                      className={`w-full px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${
                        resendSuccess
                          ? 'bg-emerald-500 text-white cursor-default'
                          : isDarkMode
                          ? 'bg-amber-500 hover:bg-amber-600 text-white'
                          : 'bg-amber-600 hover:bg-amber-700 text-white'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {resendLoading ? (
                        <>
                          <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          Sending...
                        </>
                      ) : resendSuccess ? (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Sent!
                        </>
                      ) : (
                        'Resend Email'
                      )}
                    </button>
                    {resendError && (
                      <p className={`text-xs ${isDarkMode ? 'text-red-400' : 'text-red-700'} mt-2 text-center`}>
                        {resendError}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className={`animate-fade-in ${themeClasses.glassDark} rounded-2xl p-10 text-center space-y-4 transition-all duration-500`}>
                  <div className={`inline-flex items-center justify-center w-24 h-24 ${isDarkMode ? 'bg-gradient-to-br from-white/10 to-white/5' : 'bg-gradient-to-br from-gray-100 to-gray-50'} rounded-2xl mb-4`}>
                    <Lock className={`w-12 h-12 ${themeClasses.textMuted}`} />
                  </div>
                  <h3 className={`text-2xl font-bold ${themeClasses.text}`}>Sign In to View Brackets</h3>
                  <p className={`max-w-md mx-auto text-lg ${themeClasses.textMuted}`}>
                    Brackets are available to registered users. Sign in or create an account to view bracket assignments.
                  </p>
                  <div className="flex items-center justify-center gap-3 pt-4">
                    <button
                      onClick={() => onAuthRequired()}
                      className={`${themeClasses.glass} px-5 py-2.5 rounded-lg font-semibold ${themeClasses.hoverBg} transition-all duration-300 flex items-center gap-2 ${themeClasses.text}`}
                    >
                      <LogIn className="w-4 h-4" />
                      Sign In
                    </button>
                    <button
                      onClick={() => onAuthRequired()}
                      className="bg-emerald-500 hover:bg-emerald-600 px-5 py-2.5 rounded-lg text-white font-semibold transition-all duration-300 flex items-center gap-2"
                    >
                      <UserPlus className="w-4 h-4" />
                      Sign Up
                    </button>
                  </div>
                </div>
              )
            )}

            {/* Schedule Tab */}
            {activeTab === 'schedule' && hasScheduleData && (
              currentUser?.emailVerified ? (
                <ScheduleView
                  categories={categories}
                  tournament={tournament}
                  themeClasses={themeClasses}
                  isDarkMode={isDarkMode}
                  colorTheme={colorTheme}
                />
              ) : currentUser && !currentUser.emailVerified ? (
                <div className={`animate-fade-in ${themeClasses.glassDark} rounded-2xl p-10 text-center space-y-4 transition-all duration-500`}>
                  <div className={`inline-flex items-center justify-center w-24 h-24 ${isDarkMode ? 'bg-gradient-to-br from-amber-500/20 to-amber-500/10' : 'bg-gradient-to-br from-amber-100 to-amber-50'} rounded-2xl mb-4 border-2 ${isDarkMode ? 'border-amber-500/30' : 'border-amber-300'}`}>
                    <Mail className={`w-12 h-12 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                  </div>
                  <h3 className={`text-2xl font-bold ${themeClasses.text}`}>Email Verification Required</h3>
                  <p className={`max-w-md mx-auto text-lg ${themeClasses.textMuted}`}>
                    Please verify your email address to view the match schedule.
                  </p>
                  <div className={`${isDarkMode ? 'bg-amber-500/10 border-amber-500/30' : 'bg-amber-50 border-amber-200'} border rounded-lg p-4 max-w-md mx-auto`}>
                    <p className={`text-xs ${isDarkMode ? 'text-amber-300' : 'text-amber-800'} mb-2 text-center`}>
                      Sent to: <span className="font-semibold">{currentUser.email}</span>
                    </p>
                    <p className={`text-xs ${themeClasses.textMuted} mb-3 text-center`}>
                      Check your inbox and spam folder
                    </p>
                    <button
                      onClick={onResendVerification}
                      disabled={resendLoading || resendSuccess}
                      className={`w-full px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${
                        resendSuccess
                          ? 'bg-emerald-500 text-white cursor-default'
                          : isDarkMode
                          ? 'bg-amber-500 hover:bg-amber-600 text-white'
                          : 'bg-amber-600 hover:bg-amber-700 text-white'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {resendLoading ? (
                        <>
                          <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          Sending...
                        </>
                      ) : resendSuccess ? (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Sent!
                        </>
                      ) : (
                        'Resend Email'
                      )}
                    </button>
                    {resendError && (
                      <p className={`text-xs ${isDarkMode ? 'text-red-400' : 'text-red-700'} mt-2 text-center`}>
                        {resendError}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className={`animate-fade-in ${themeClasses.glassDark} rounded-2xl p-10 text-center space-y-4 transition-all duration-500`}>
                  <div className={`inline-flex items-center justify-center w-24 h-24 ${isDarkMode ? 'bg-gradient-to-br from-white/10 to-white/5' : 'bg-gradient-to-br from-gray-100 to-gray-50'} rounded-2xl mb-4`}>
                    <Lock className={`w-12 h-12 ${themeClasses.textMuted}`} />
                  </div>
                  <h3 className={`text-2xl font-bold ${themeClasses.text}`}>Sign In to View Schedule</h3>
                  <p className={`max-w-md mx-auto text-lg ${themeClasses.textMuted}`}>
                    The match schedule is available to registered users. Sign in or create an account to view court assignments and match times.
                  </p>
                  <div className="flex items-center justify-center gap-3 pt-4">
                    <button
                      onClick={() => onAuthRequired()}
                      className={`${themeClasses.glass} px-5 py-2.5 rounded-lg font-semibold ${themeClasses.hoverBg} transition-all duration-300 flex items-center gap-2 ${themeClasses.text}`}
                    >
                      <LogIn className="w-4 h-4" />
                      Sign In
                    </button>
                    <button
                      onClick={() => onAuthRequired()}
                      className="bg-emerald-500 hover:bg-emerald-600 px-5 py-2.5 rounded-lg text-white font-semibold transition-all duration-300 flex items-center gap-2"
                    >
                      <UserPlus className="w-4 h-4" />
                      Sign Up
                    </button>
                  </div>
                </div>
              )
            )}

            {/* Live Results Tab */}
            {activeTab === 'results' && (
              !currentUser ? (
                <div className={`animate-fade-in ${themeClasses.glassDark} rounded-2xl p-10 text-center space-y-4 transition-all duration-500`}>
                  <div className={`inline-flex items-center justify-center w-24 h-24 ${isDarkMode ? 'bg-gradient-to-br from-white/10 to-white/5' : 'bg-gradient-to-br from-gray-100 to-gray-50'} rounded-2xl mb-4`}>
                    <Lock className={`w-12 h-12 ${themeClasses.textMuted}`} />
                  </div>
                  <h3 className={`text-2xl font-bold ${themeClasses.text}`}>Sign In to View {isCompleted ? 'Results' : 'Live Scores'}</h3>
                  <p className={`max-w-md mx-auto text-lg ${themeClasses.textMuted}`}>
                    {isCompleted ? 'Results are' : 'Live scores are'} available to registered users. Sign in or create an account to follow the action.
                  </p>
                  <div className="flex items-center justify-center gap-3 pt-4">
                    <button
                      onClick={() => onAuthRequired()}
                      className={`${themeClasses.glass} px-5 py-2.5 rounded-lg font-semibold ${themeClasses.hoverBg} transition-all duration-300 flex items-center gap-2 ${themeClasses.text}`}
                    >
                      <LogIn className="w-4 h-4" />
                      Sign In
                    </button>
                    <button
                      onClick={() => onAuthRequired()}
                      className="bg-emerald-500 hover:bg-emerald-600 px-5 py-2.5 rounded-lg text-white font-semibold transition-all duration-300 flex items-center gap-2"
                    >
                      <UserPlus className="w-4 h-4" />
                      Sign Up
                    </button>
                  </div>
                </div>
              ) : currentUser && !currentUser.emailVerified ? (
                <div className={`animate-fade-in ${themeClasses.glassDark} rounded-2xl p-10 text-center space-y-4 transition-all duration-500`}>
                  <div className={`inline-flex items-center justify-center w-24 h-24 ${isDarkMode ? 'bg-gradient-to-br from-amber-500/20 to-amber-500/10' : 'bg-gradient-to-br from-amber-100 to-amber-50'} rounded-2xl mb-4 border-2 ${isDarkMode ? 'border-amber-500/30' : 'border-amber-300'}`}>
                    <Mail className={`w-12 h-12 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                  </div>
                  <h3 className={`text-2xl font-bold ${themeClasses.text}`}>Email Verification Required</h3>
                  <p className={`max-w-md mx-auto text-lg ${themeClasses.textMuted}`}>
                    Please verify your email address to view {isCompleted ? 'results' : 'live scores'}.
                  </p>
                  <div className={`${isDarkMode ? 'bg-amber-500/10 border-amber-500/30' : 'bg-amber-50 border-amber-200'} border rounded-lg p-4 max-w-md mx-auto`}>
                    <p className={`text-xs ${isDarkMode ? 'text-amber-300' : 'text-amber-800'} mb-2 text-center`}>
                      Sent to: <span className="font-semibold">{currentUser.email}</span>
                    </p>
                    <p className={`text-xs ${themeClasses.textMuted} mb-3 text-center`}>
                      Check your inbox and spam folder
                    </p>
                    <button
                      onClick={onResendVerification}
                      disabled={resendLoading || resendSuccess}
                      className={`w-full px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${
                        resendSuccess
                          ? 'bg-emerald-500 text-white cursor-default'
                          : isDarkMode
                          ? 'bg-amber-500 hover:bg-amber-600 text-white'
                          : 'bg-amber-600 hover:bg-amber-700 text-white'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {resendLoading ? (
                        <>
                          <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          Sending...
                        </>
                      ) : resendSuccess ? (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Sent!
                        </>
                      ) : (
                        'Resend Email'
                      )}
                    </button>
                    {resendError && (
                      <p className={`text-xs ${isDarkMode ? 'text-red-400' : 'text-red-700'} mt-2 text-center`}>
                        {resendError}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
              <div className="space-y-5 animate-fade-in">
                <div className="flex items-center justify-between">
                  <h2 className={`text-2xl sm:text-3xl font-black tracking-tight ${themeClasses.text}`}>{isCompleted ? 'Results' : 'Live Scores'}</h2>
                  {isInProgress && (
                    <div className="flex items-center gap-2 text-emerald-400 text-sm font-semibold">
                      <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></div>
                      Live
                    </div>
                  )}
                </div>
                {!isActive ? (
                  <div className={`${themeClasses.glassDark} rounded-2xl p-10 text-center space-y-4 transition-all duration-500`}>
                    <div className={`inline-flex items-center justify-center w-24 h-24 ${isDarkMode ? 'bg-gradient-to-br from-white/10 to-white/5' : 'bg-gradient-to-br from-gray-100 to-gray-50'} rounded-2xl mb-4`}>
                      <Trophy className={`w-12 h-12 ${themeClasses.textMuted}`} />
                    </div>
                    <h3 className={`text-2xl font-bold ${themeClasses.text}`}>Tournament Not Yet Started</h3>
                    <p className={`max-w-md mx-auto text-lg ${themeClasses.textMuted}`}>
                      Live scores will be available once the tournament has started. Check back soon!
                    </p>
                  </div>
                ) : (
                  <div className="space-y-5">
                    {/* Day Filter */}
                    {resultsDateKeys.length > 1 && (
                      <div className="flex flex-wrap gap-2">
                        {resultsDateKeys.map((dateKey) => {
                          const isActiveDay = dateKey === resultsDay
                          return (
                            <button
                              key={dateKey}
                              onClick={() => {
                            setResultsDay(dateKey)
                            trackLiveScoresActivity('Day Selected', { label: dateKey === 'No Date' ? 'Unscheduled' : new Date(dateKey).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) })
                          }}
                              className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-semibold border transition-all duration-200 ${
                                isActiveDay
                                  ? isDarkMode
                                    ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40'
                                    : 'bg-emerald-50 text-emerald-700 border-emerald-300'
                                  : isDarkMode
                                    ? 'bg-white/[0.04] text-white/50 border-white/[0.08] hover:bg-white/[0.08] hover:text-white/70'
                                    : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100 hover:text-gray-700'
                              }`}
                            >
                              <Calendar className="w-3.5 h-3.5" />
                              {dateKey === 'No Date' ? 'Unscheduled' : new Date(dateKey).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                            </button>
                          )
                        })}
                      </div>
                    )}

                    <div className="grid gap-4 sm:gap-5">
                    {sortedCategories.filter(c => {
                      if (!resultsDay) return true
                      const catDate = (c.startDate || tournament.startDate)
                        ? new Date(c.startDate || tournament.startDate).toDateString()
                        : 'No Date'
                      return catDate === resultsDay
                    }).map((category) => {
                      const totalMatches = category.matchStats?.total
                        ?? category.matches?.filter(m => m.team1Id && m.team2Id).length ?? 0
                      const completedMatches = category.matchStats?.completed
                        ?? category.matches?.filter(m => m.status === 'completed').length ?? 0
                      const progress = totalMatches > 0 ? (completedMatches / totalMatches) * 100 : 0

                      return (
                        <button
                          key={category.id}
                          onClick={() => {
                            navigateToCategory(category.id)
                            trackLiveScoresActivity('Category Selected', { label: category.title, category_id: category.id, value: Math.round(progress) })
                          }}
                          className={`group w-full ${themeClasses.cardBg} rounded-2xl p-5 sm:p-6 border transition-all duration-500 text-left ${themeClasses.cardHover} animate-content-update`}
                        >
                          <div className="flex items-start justify-between mb-5">
                            <div className="flex-1">
                              <h3 className={`text-xl sm:text-2xl font-black mb-2 tracking-tight ${themeClasses.text} group-hover:text-emerald-400 transition-colors duration-300`}>
                                {category.title}
                                {(category.skillLevel || category.ageGroup) && (
                                  <span className={`ml-2 text-base sm:text-lg font-semibold ${themeClasses.textMuted}`}>
                                    ({[category.skillLevel, category.ageGroup].filter(Boolean).join(' • ')})
                                  </span>
                                )}
                              </h3>
                              {category.description && (
                                <p className={`text-sm whitespace-pre-wrap hidden md:block ${themeClasses.textMuted}`}>
                                  {category.description}
                                </p>
                              )}
                            </div>
                            <div className={`relative`}>
                              <div className={`absolute -inset-1 bg-gradient-to-r ${themeClasses.accent} rounded-xl blur opacity-40 group-hover:opacity-60 transition-opacity duration-300`}></div>
                              <div className={`relative bg-gradient-to-br ${themeClasses.accent} rounded-xl p-3 shadow-lg`}>
                                <Award className="w-6 h-6 text-white" />
                              </div>
                            </div>
                          </div>

                          {/* Stats */}
                          <div className="grid grid-cols-3 gap-3 mb-5">
                            <div className={`${isDarkMode ? 'bg-white/[0.04]' : 'bg-gray-50'} rounded-xl p-3 sm:p-4 text-center border ${themeClasses.border} transition-all duration-300`}>
                              <Users className={`w-5 h-5 ${colorTheme.text.primary} mx-auto mb-1.5`} />
                              <p className={`text-xs font-semibold uppercase tracking-wider ${themeClasses.textMuted}`}>Teams</p>
                              <p className={`text-xl sm:text-2xl font-black ${themeClasses.text}`}>{category.matchStats?.teamCount ?? category.teams?.length ?? 0}</p>
                            </div>
                            <div className={`${isDarkMode ? 'bg-white/[0.04]' : 'bg-gray-50'} rounded-xl p-3 sm:p-4 text-center border ${themeClasses.border} transition-all duration-300`}>
                              <List className={`w-5 h-5 ${colorTheme.text.secondary} mx-auto mb-1.5`} />
                              <p className={`text-xs font-semibold uppercase tracking-wider ${themeClasses.textMuted}`}>Matches</p>
                              <p className={`text-xl sm:text-2xl font-black ${themeClasses.text}`}>{totalMatches}</p>
                            </div>
                            <div className={`${isDarkMode ? 'bg-white/[0.04]' : 'bg-gray-50'} rounded-xl p-3 sm:p-4 text-center border ${themeClasses.border} transition-all duration-300`}>
                              <TrendingUp className={`w-5 h-5 text-emerald-400 mx-auto mb-1.5`} />
                              <p className={`text-xs font-semibold uppercase tracking-wider ${themeClasses.textMuted}`}>Done</p>
                              <p className={`text-xl sm:text-2xl font-black ${themeClasses.text}`}>{completedMatches}</p>
                            </div>
                          </div>

                          {/* Premium Progress Bar */}
                          <div className="relative">
                            <div className={`${isDarkMode ? 'bg-white/10' : 'bg-gray-200'} rounded-full h-2.5 overflow-hidden`}>
                              <div
                                className="bg-gradient-to-r from-emerald-500 via-teal-400 to-emerald-500 h-full transition-all duration-700 ease-out relative overflow-hidden"
                                style={{ width: `${progress}%` }}
                              >
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
                              </div>
                            </div>
                            <div className="flex justify-between items-center mt-2">
                              <p className={`text-xs ${themeClasses.textMuted}`}>{completedMatches} of {totalMatches} matches</p>
                              <p className={`text-sm font-bold text-emerald-400`}>
                                {progress.toFixed(0)}%
                              </p>
                            </div>
                          </div>
                        </button>
                      )
                    })}
                    </div>
                  </div>
                )}
              </div>
              )
            )}
            
            {/* Description Tab */}
            {activeTab === 'description' && (
              <div className="space-y-4 animate-fade-in">
                <h2 className={`text-2xl font-bold mb-4 ${themeClasses.text}`}>Tournament Description</h2>
                {tournament.description ? (
                  <p className={`whitespace-pre-wrap leading-relaxed ${themeClasses.textMuted}`}>
                    {tournament.description}
                  </p>
                ) : (
                  <p className={`italic ${themeClasses.textSubtle}`}>No description available</p>
                )}
              </div>
            )}
            
            {/* Official Rules Tab */}
            {activeTab === 'rules' && (
              <div className="space-y-4 animate-fade-in">
                <h2 className={`text-2xl font-bold mb-4 ${themeClasses.text}`}>Official Rules</h2>
                {tournament.rules ? (
                  <div className={`whitespace-pre-wrap leading-relaxed ${themeClasses.textMuted}`}>
                    {tournament.rules}
                  </div>
                ) : (
                  <p className={`italic ${themeClasses.textSubtle}`}>No official rules available</p>
                )}
              </div>
            )}
            
            {/* Skill Levels Tab */}
            {activeTab === 'skillLevels' && (
              <div className="space-y-4 animate-fade-in">
                <h2 className={`text-2xl font-bold mb-4 ${themeClasses.text}`}>Skill Levels</h2>
                {tournament.skillLevels && tournament.skillLevels.length > 0 ? (
                  <div className="space-y-4">
                    {tournament.skillLevels.map((level, index) => (
                      <div key={level.id || index} className={`${themeClasses.glassDark} rounded-xl p-5 space-y-2 transition-colors duration-300`}>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500/20 to-teal-500/20">
                            <Target className="w-5 h-5 text-emerald-400" />
                          </div>
                          <h3 className={`text-xl font-bold ${themeClasses.text}`}>{level.name}</h3>
                        </div>
                        {level.description && (
                          <p className={`leading-relaxed pl-13 whitespace-pre-wrap ${themeClasses.textMuted}`}>
                            {level.description}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={`${themeClasses.glassDark} rounded-xl p-10 text-center`}>
                    <div className={`inline-flex items-center justify-center w-16 h-16 ${isDarkMode ? 'bg-gradient-to-br from-white/10 to-white/5' : 'bg-gradient-to-br from-gray-100 to-gray-50'} rounded-xl mb-4`}>
                      <Target className={`w-8 h-8 ${themeClasses.textMuted}`} />
                    </div>
                    <p className={`italic ${themeClasses.textSubtle}`}>No skill levels defined for this tournament</p>
                  </div>
                )}
              </div>
            )}
            
            {/* Tournament Information Tab */}
            {activeTab === 'info' && (
              <div className="space-y-6 animate-fade-in">
                <h2 className={`text-2xl font-bold mb-4 ${themeClasses.text}`}>Tournament Information</h2>
                
                {/* Date & Time */}
                <div className={`${themeClasses.glassDark} rounded-xl p-4 space-y-3 transition-colors duration-300`}>
                  <h3 className={`text-lg font-bold flex items-center gap-2 ${themeClasses.text}`}>
                    <Clock className="w-5 h-5 text-emerald-400" />
                    Date & Time
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {tournament.startDate && (
                      <div>
                        <p className={`text-xs font-semibold ${themeClasses.textMuted}`}>Start Date & Time</p>
                        <p className={themeClasses.text}>{formatDateTime(tournament.startDate)}</p>
                      </div>
                    )}
                    {tournament.endDate && (
                      <div>
                        <p className={`text-xs font-semibold ${themeClasses.textMuted}`}>End Date & Time</p>
                        <p className={themeClasses.text}>{formatDateTime(tournament.endDate)}</p>
                      </div>
                    )}
                    {!tournament.startDate && !tournament.endDate && (
                      <p className={`italic ${themeClasses.textSubtle}`}>No date information available</p>
                    )}
                  </div>
                </div>
                
                {/* Categories */}
                <div className={`${themeClasses.glassDark} rounded-xl p-4 space-y-4 transition-colors duration-300`}>
                  <h3 className={`text-lg font-bold flex items-center gap-2 ${themeClasses.text}`}>
                    <Award className="w-5 h-5 text-emerald-400" />
                    Categories ({categories.length})
                  </h3>
                  
                  {/* Disclaimer */}
                  <div className={`${isDarkMode ? 'bg-blue-900/20 border-blue-500/50 text-blue-400' : 'bg-blue-50 border-blue-200 text-blue-700'} border rounded-lg p-3 text-sm`}>
                    <p className="font-semibold flex items-center gap-2">
                      <Info className="w-4 h-4" />
                      Important Notice
                    </p>
                    <p className="text-xs mt-1">
                      A minimum of 16 players (8 pairs) are required for each category to proceed. Categories with fewer registrations may be cancelled or merged.
                    </p>
                  </div>
                  
                  <div className="space-y-6">
                    {sortedDateGroups.map(([dateKey, dateCategories], index) => {
                      // Sort categories within this date group
                      const sortedDateCategories = [...dateCategories].sort((a, b) => {
                        const titleCompare = (a.title || '').localeCompare(b.title || '')
                        if (titleCompare !== 0) return titleCompare
                        
                        if (tournament.skillLevels && tournament.skillLevels.length > 0) {
                          const aSkillIndex = tournament.skillLevels.findIndex(sl => sl.name === a.skillLevel)
                          const bSkillIndex = tournament.skillLevels.findIndex(sl => sl.name === b.skillLevel)
                          
                          if (aSkillIndex !== -1 && bSkillIndex !== -1) {
                            const skillCompare = aSkillIndex - bSkillIndex
                            if (skillCompare !== 0) return skillCompare
                          }
                          if (aSkillIndex !== -1 && bSkillIndex === -1) return -1
                          if (aSkillIndex === -1 && bSkillIndex !== -1) return 1
                        }
                        
                        return (a.ageGroup || '').localeCompare(b.ageGroup || '')
                      })
                      
                      const isExpanded = expandedDateGroups[dateKey] !== false
                      
                      return (
                        <div key={dateKey} className="space-y-3">
                          {/* Date Header - Clickable */}
                          <div 
                            className={`flex items-center gap-2 pb-2 border-b ${themeClasses.border} cursor-pointer hover:opacity-80 transition-opacity`}
                            onClick={() => toggleDateGroup(dateKey)}
                          >
                            <Calendar className="w-4 h-4 text-emerald-400" />
                            {dateKey !== 'No Date' && (
                              <span className={`px-2 py-0.5 rounded-md text-xs font-bold bg-emerald-500/20 text-emerald-400`}>
                                Day {index + 1}
                              </span>
                            )}
                            <h4 className={`font-bold text-sm ${themeClasses.text}`}>
                              {dateKey === 'No Date' ? 'No Specific Date' : formatDate(dateKey)}
                            </h4>
                            <span className={`text-xs ${themeClasses.textMuted}`}>({dateCategories.length} {dateCategories.length === 1 ? 'category' : 'categories'})</span>
                            <div className="ml-auto">
                              {isExpanded ? (
                                <ChevronUp className={`w-4 h-4 ${themeClasses.textMuted}`} />
                              ) : (
                                <ChevronDown className={`w-4 h-4 ${themeClasses.textMuted}`} />
                              )}
                            </div>
                          </div>
                          
                          {/* Categories for this date - Collapsible with Animation */}
                          <div 
                            className={`overflow-hidden transition-all duration-500 ease-in-out ${
                              isExpanded ? 'max-h-[10000px] opacity-100' : 'max-h-0 opacity-0'
                            }`}
                          >
                            <div className="space-y-3 pt-3">
                            {sortedDateCategories.map((category) => {
                      const registrationDeadline = category.registrationDeadline ? new Date(category.registrationDeadline) : null
                      const now = new Date()
                      const isRegistrationOpen = registrationDeadline ? registrationDeadline > now : false
                      
                      // Get registration count from real-time counter, fall back to static JSON data
                      const registrationCount = registrationCounts[category.id]?.count || (category.registrations ? category.registrations.length : 0)
                      const maxTeams = category.maxTeams || 0
                      const numberOfPlayers = category.numberOfPlayers || 2
                      const maxRegistrations = maxTeams * numberOfPlayers
                      
                      const hasSpaceAvailable = maxTeams === 0 || registrationCount < maxRegistrations
                      const isTournamentUpcoming = tournament.status === 'upcoming'
                      const canRegister = !isTournamentUpcoming && !isRegistrationClosed && isRegistrationOpen && hasSpaceAvailable
                      
                      return (
                        <div key={category.id} className={`${themeClasses.cardBg} rounded-lg p-4 border transition-colors duration-300`}>
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <h4 className={`font-bold mb-2 ${themeClasses.text}`}>
                                {category.title}
                                {(category.skillLevel || category.ageGroup) && (
                                  <span className={`ml-2 text-sm font-semibold ${themeClasses.textMuted}`}>
                                    ({[category.skillLevel, category.ageGroup].filter(Boolean).join(' • ')})
                                  </span>
                                )}
                              </h4>
                              
                              {/* Start Date */}
                              {category.startDate && (
                                <div className="flex items-center gap-2 mb-2 text-emerald-400">
                                  <Calendar className="w-4 h-4" />
                                  <span className="text-sm font-semibold">{formatDateTime(category.startDate)}</span>
                                </div>
                              )}
                              
                              {/* Registration Deadline */}
                              {registrationDeadline && (
                                <div className={`flex items-center gap-2 mb-2 ${isRegistrationOpen ? 'text-blue-400' : 'text-gray-500'}`}>
                                  <Clock className="w-4 h-4" />
                                  <div className="text-sm">
                                    <span className="font-semibold">Reg. Deadline: </span>
                                    <span>{formatDate(category.registrationDeadline)}</span>
                                    {!isRegistrationOpen && <span className="ml-2 text-red-400">(Closed)</span>}
                                  </div>
                                </div>
                              )}
                            </div>
                            
                            {/* Register Button or Status Message */}
                            {isTournamentUpcoming ? (
                              <div className="text-center">
                                <div className={`text-xs font-semibold ${themeClasses.textMuted} mb-1`}>Registration</div>
                                <div className="text-xs text-blue-400 font-medium">Opens Soon</div>
                              </div>
                            ) : canRegister ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  if (!currentUser) {
                                    onAuthRequired()
                                  } else if (onRegister) {
                                    onRegister(category)
                                  }
                                }}
                                className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-2 px-4 rounded-lg transition-colors text-sm whitespace-nowrap"
                              >
                                Register
                              </button>
                            ) : null}
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            {category.entryFee && (
                              <div>
                                <p className={`text-xs font-semibold ${themeClasses.textMuted}`}>Entry Fee</p>
                                <p className={`font-bold text-emerald-400`}>
                                  ₱{category.entryFee.toLocaleString()} <span className="text-xs font-normal">per player</span>
                                </p>
                              </div>
                            )}
                            <div>
                              {tournament.status === 'registration_open' || tournament.status === 'upcoming' ? (
                                <>
                                  <p className={`text-xs font-semibold ${themeClasses.textMuted}`}>Available Entries</p>
                                  <p className={`font-bold ${hasSpaceAvailable ? 'text-emerald-400' : 'text-red-400'}`}>
                                    {tournament.status === 'upcoming' 
                                      ? `${maxRegistrations || '∞'} players`
                                      : `${registrationCount}/${maxRegistrations || '∞'} players`
                                    }
                                    {!hasSpaceAvailable && tournament.status !== 'upcoming' && <span className="block text-xs">(Full)</span>}
                                  </p>
                                </>
                              ) : (
                                <>
                                  <p className={`text-xs font-semibold ${themeClasses.textMuted}`}>Registered</p>
                                  <p className={`font-bold text-emerald-400`}>
                                    {registrationCount} {registrationCount === 1 ? 'player' : 'players'}
                                  </p>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
                
                {/* Venue & Location */}
                <div className={`${themeClasses.glassDark} rounded-xl p-4 space-y-3 transition-colors duration-300`}>
                  <h3 className={`text-lg font-bold flex items-center gap-2 ${themeClasses.text}`}>
                    <MapPin className="w-5 h-5 text-blue-400" />
                    Venue & Location
                  </h3>
                  <div className="space-y-3">
                    {tournament.venue && (
                      <div>
                        <p className={`text-xs font-semibold ${themeClasses.textMuted}`}>Venue</p>
                        <p className={themeClasses.text}>{tournament.venue}</p>
                      </div>
                    )}
                    {tournament.location && (
                      <div>
                        <p className={`text-xs font-semibold ${themeClasses.textMuted}`}>Location</p>
                        <p className={themeClasses.text}>{tournament.location}</p>
                      </div>
                    )}
                    {!tournament.venue && !tournament.location && (
                      <p className={`italic ${themeClasses.textSubtle}`}>No venue information available</p>
                    )}
                    
                    {/* Google Maps Embed */}
                    {tournament.location && (
                      <div className="mt-3">
                        <iframe
                          title="Tournament Location"
                          width="100%"
                          height="250"
                          style={{ border: 0 }}
                          loading="lazy"
                          allowFullScreen
                          referrerPolicy="no-referrer-when-downgrade"
                          src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(tournament.location)}&maptype=roadmap`}
                          className="rounded-lg"
                        ></iframe>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Contact Information */}
                {(tournament.contactEmail || tournament.contactMobile) && (
                  <div className={`${themeClasses.glassDark} rounded-xl p-4 space-y-3 transition-colors duration-300`}>
                    <h3 className={`text-lg font-bold flex items-center gap-2 ${themeClasses.text}`}>
                      <Mail className="w-5 h-5 text-purple-400" />
                      Contact Information
                    </h3>
                    <div className="space-y-2">
                      {tournament.contactEmail && (
                        <div>
                          <p className={`text-xs font-semibold ${themeClasses.textMuted}`}>Email</p>
                          <a href={`mailto:${tournament.contactEmail}`} className={`${themeClasses.text} hover:text-blue-400 transition-colors`}>
                            {tournament.contactEmail}
                          </a>
                        </div>
                      )}
                      {tournament.contactMobile && (
                        <div>
                          <p className={`text-xs font-semibold ${themeClasses.textMuted}`}>Mobile</p>
                          <a href={`tel:${tournament.contactMobile}`} className={`${themeClasses.text} hover:text-blue-400 transition-colors`}>
                            {tournament.contactMobile}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
          </div>
          
          {/* Sponsors Sidebar - Desktop: Right sidebar, Mobile: Bottom section */}
          <div className="w-full lg:w-80 flex-shrink-0 order-2">
            <div className={`${themeClasses.glass} rounded-2xl shadow-xl p-4 lg:sticky lg:top-24 transition-colors duration-300`}>
              <div className={`flex items-center gap-2 mb-3 pb-3 border-b ${themeClasses.border}`}>
                <Heart className="w-4 h-4 text-pink-400" />
                <h3 className={`text-sm font-bold ${themeClasses.text}`}>Sponsors</h3>
              </div>
              
              {tournament.sponsorshipLevels && tournament.sponsorshipLevels.length > 0 ? (
                <div className="space-y-4 lg:max-h-[calc(100vh-200px)] lg:overflow-y-auto lg:pr-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4 lg:gap-0 lg:space-y-4">
                  {tournament.sponsorshipLevels.map((level, levelIndex) => (
                    level.sponsors && level.sponsors.length > 0 && (
                      <div key={levelIndex} className="space-y-2">
                        {tournament.sponsorshipLevels.length > 1 && (
                          <div className="flex items-center gap-1.5">
                            <div className="w-0.5 h-4 rounded-full" style={{ backgroundColor: level.color || '#10b981' }}></div>
                            <h4 className={`text-xs font-semibold ${themeClasses.textMuted}`}>{level.name}</h4>
                          </div>
                        )}
                        <div className="space-y-1.5">
                          {level.sponsors.map((sponsor, sponsorIndex) => (
                            <div 
                              key={sponsorIndex} 
                              className={`${themeClasses.glassDark} rounded-lg p-2.5 border-l-2 ${themeClasses.hoverBg} transition-all duration-300 cursor-pointer flex items-center gap-3`}
                              style={{ borderColor: level.color || '#10b981' }}
                              onClick={() => sponsor.website && window.open(sponsor.website, '_blank')}
                            >
                              {sponsor.logoUrl ? (
                                <img
                                  src={sponsor.logoUrl}
                                  alt={sponsor.name || 'Sponsor'}
                                  className={`w-20 h-20 flex-shrink-0 object-contain rounded-lg ${isDarkMode ? 'bg-white/5' : 'bg-gray-100'} p-2`}
                                  onError={(e) => e.target.style.display = 'none'}
                                />
                              ) : (
                                <div className={`w-20 h-20 flex-shrink-0 rounded-lg ${isDarkMode ? 'bg-white/10' : 'bg-gray-100'} flex items-center justify-center`}>
                                  <Heart className="w-10 h-10 text-pink-400" />
                                </div>
                              )}
                              {sponsor.name && (
                                <p className={`font-medium text-sm flex-1 line-clamp-2 ${themeClasses.text}`}>{sponsor.name}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  ))}
                </div>
              ) : (
                <p className={`italic text-xs ${themeClasses.textSubtle}`}>No sponsors</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TournamentView
